import { environment } from '@/environments/environment';
import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { CookieService } from 'ngx-cookie-service';
import {
  BehaviorSubject,
  catchError,
  EMPTY,
  firstValueFrom,
  map,
  of,
  switchMap,
  take,
  tap,
  throwError,
} from 'rxjs';
import { REFRESH_TOKEN, TOKEN } from '../keys/key';
import { handleError } from '../tools/handle-error.tools';
import { ErrorLogService } from './error-log.service';
import { ErrorLog } from '@/app/models/interface/error-log.model';
import { LoginResponse } from '@/app/models/interface/login-response.model';
import { Login } from '@/app/models/interface/login.model';
import { CustomResponse } from '@/app/models/interface/response.model';
import { Usuario } from '@/app/models/interface/usuario.model';

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly http = inject(HttpClient);
  private readonly url = `${environment.api}auth/`;

  login(credenciales: Login, param?: Params, captchaToken?: string) {
    const dir = `${this.url}login`;

    const headers: any = {};

    headers['device'] = 'web';

    if (captchaToken) headers['x-captcha-token'] = captchaToken;

    return this.http
      .post<CustomResponse<LoginResponse>>(dir, credenciales, { headers })
      .pipe(
        catchError((err) => handleError(err)),
        map((data) => data.result),
        tap((data) => {
          this.saveToken(data.token, data.refreshToken);
          this.pushNewUser(data.data);
          this.redirect(param);
        }),
      );
  }

  refreshToken() {
    const dir = this.url + 'refreshToken';

    const refreshToken = this.cookie.get(REFRESH_TOKEN);

    const headers: any = {};

    headers['device'] = 'web';

    return this.http
      .post<{
        ok: true;
        result: string;
        msg: string;
      }>(dir, { refreshToken }, { headers })
      .pipe(
        catchError((err) => throwError(() => err)),
        map((data) => data.result),
        tap((token) => this.saveToken(token, refreshToken)),
      );
  }

  //* Inyección de el servicio de Cookies --------------------->
  private readonly cookie = inject(CookieService);
  /** @description Guarda el token en las cookies */
  private saveToken(token: string, refreshToken: string) {
    const options = {
      path: '/',
      sameSite: 'Strict' as const,
      secure: true,
    };

    this.cookie.set(
      REFRESH_TOKEN,
      refreshToken,
      undefined,
      options.path,
      undefined,
      options.secure,
      options.sameSite,
    );
    this.cookie.set(
      TOKEN,
      token,
      undefined,
      options.path,
      undefined,
      options.secure,
      options.sameSite,
    );
  }

  /**
   * Elimina los tokens almacenados en las cookies.
   */
  private removeToken(): void {
    this.cookie.delete(TOKEN, '/');
    this.cookie.delete(REFRESH_TOKEN, '/');
  }

  /**
   * Limpia la sesión local y redirige al login.
   * No necesita que el backend esté disponible.
   */
  forceLogout(): void {
    this.removeToken();
    this.router.navigate(['/ingresar'], { replaceUrl: true });
  }

  /**
   * Cierra la sesión en el backend y limpia el cliente.
   */
  logout() {
    const url = this.url + 'logout';
    const refreshToken = this.cookie.get(REFRESH_TOKEN);

    // Aunque no tengamos refresh token, debemos limpiar la sesión local.
    if (!refreshToken) {
      this.forceLogout();
      return EMPTY;
    }

    return this.http.post(url, { refreshToken }).pipe(
      catchError(() => of(null)),
      tap(() => this.forceLogout()),
    );
  }
  // /** @description Remueve el token de las cookies */
  // private removeToken() {
  //   this.cookie.delete(TOKEN, '/');
  //   this.cookie.delete(REFRESH_TOKEN, '/');
  // }

  // forceLogout() {
  //   this.removeToken();
  //   this.router.navigate(['ingresar'], { replaceUrl: true });
  // }

  //* Inyección del servicio de Router para navegar ------------>
  private readonly router = inject(Router);
  // logout() {
  //   const url = this.url + 'logout';

  //   const refreshToken = this.cookie.get(REFRESH_TOKEN);

  //   return this.http.post(url, { refreshToken }).pipe(
  //     tap((data) => {
  //       this.removeToken();

  //       this.router.navigate(['ingresar'], { replaceUrl: true });
  //     }),
  //     catchError((err) => {
  //       return throwError(() => err);
  //     }),
  //   );
  // }
  /**
   * @description
   * Una vez que pasa las autenticaciones, redirigimos al usuario.
   * @param param Un param en la url para redirigir a una sección específica
   * */
  private redirect(param: any) {
    if (param ? param['redirect'] : false) {
      const ruta = param['redirect'];
      const p = { ...param };
      delete p['redirect'];
      this.router.navigate([ruta], { queryParams: p });
    } else this.router.navigate(['home']);
  }

  //* Guardamos el estado del Usuario -------------------------->
  private usuario = new BehaviorSubject<Usuario | null>(null);
  /** @description Guardamos el usuario en una variable tipo Behavior Subject */
  private pushNewUser(usuario: Usuario) {
    usuario.competencias =
      usuario.actividadCompetenciaUsuario?.map(
        (actividad) => actividad.competencia,
      ) || [];

    usuario.roles =
      usuario.usuarioRoles?.map((usuarioRol) => usuarioRol.rol) || [];

    usuario.subRoles =
      usuario.usuarioSubRoles?.map((usuarioSubRol) => usuarioSubRol.subRol) ||
      [];

    const {
      actividadCompetenciaUsuario,
      usuarioRoles,
      usuarioSubRoles,
      ...rest
    } = usuario;

    this.usuario.next(rest as Usuario);
  }
  /**
   * @description Retorna la información del usuario
   * @returns Información del usuario
   */
  async returnUserInfo(): Promise<Usuario | null> {
    const direction = `${this.url}info`;
    try {
      //* Obtener la información del usuario, ya sea desde el observable local o desde la API
      const userData = await firstValueFrom(this.getUserData(direction));

      return userData;
    } catch (error) {
      //* Manejar errores y cerrar sesión
      // this.handleLogout(error);
      return null;
    }
  }
  /**
   * @description
   * Intenta obtener la información del usuario desde el observable `usuario`.
   * Si `usuario` es `null`, realiza una solicitud HTTP para obtener los datos.
   */
  private getUserData(direction: string) {
    return this.usuario.pipe(
      take(1),
      switchMap((data) =>
        data != null ? of(data) : this.fetchAndCacheUser(direction),
      ),
    );
  }
  /**
   * @description
   * Realiza una solicitud HTTP para obtener los datos del usuario y
   * actualiza el observable `usuario` con el nuevo valor.
   */
  private fetchAndCacheUser(direction: string) {
    return this.http.get<CustomResponse<Usuario>>(direction).pipe(
      map((data) => data.result),
      tap((userToken) => this.pushNewUser(userToken)),
      catchError(() => of(null)),
      // catchError((error) => {
      //   this.handleLogout(error);
      //   return throwError(() => error);
      // }),
    );
  }
  /**
   * @description
   * Maneja el cierre de sesión y los errores arrojados durante la solicitud de datos.
   * Puede ampliarse para incluir más acciones de limpieza si es necesario.
   */
  // private handleLogout(error?: any) {
  //   //! Log para más contexto del error
  //   console.error('Error al recuperar información de usuario:', error);

  //   this.createErrorLog(error);

  //   this.logout().subscribe();
  // }

  // private readonly errorLogService = inject(ErrorLogService);

  // private createErrorLog(err: any) {
  //   const data: Partial<ErrorLog> = {
  //     descripcion: JSON.stringify(err),
  //     url: window.location.href,
  //   };

  //   this.errorLogService.create(data).subscribe((data) => {
  //     console.info('Log creado:', data);
  //   });
  // }

  //* Comprobamos el token ------------------------------------>
  async checkToken(token: string) {
    const direction = `${this.url}check`;
    let isValid = false;
    try {
      isValid = (
        await firstValueFrom(
          this.http.post<CustomResponse<boolean>>(direction, { token }),
        )
      ).result;
    } catch (error) {
      isValid = false;
    }
    return isValid;
  }

  private readonly route = inject(ActivatedRoute);
  async navigateToHome() {
    //! Recordar que el parámetro para la re dirección es ?redirect=/home...
    const params = await firstValueFrom(this.route.queryParams);
    const token = this.cookie.get(TOKEN);
    if (token) {
      if (params['redirect']) {
        const ruta = params['redirect'];
        const queryParams = { ...params };
        delete queryParams['redirect'];
        this.router.navigate([ruta], { queryParams });
      } else this.router.navigate(['home']);
    }
  }

  register(data: Partial<Usuario>) {
    const url = this.url + 'register';
    return this.http
      .post<CustomResponse<{ usuario: Usuario; error: boolean }>>(url, data)
      .pipe(
        catchError((err) => handleError(err)),
        map((data) => {
          const d = data.result.usuario;
          return {
            error: data.result.error,
            usuario: {
              ...d,
              competencias:
                d.usuarioCompetencia?.map((m) => m.competencia) || [],
              subCompetencias:
                d.usuarioSubCompetencia?.map((m) => m.subCompetencia) || [],
              roles: d.usuarioRoles?.map((m) => m.rol) || [],
              subRoles: d.usuarioSubRoles?.map((m) => m.subRol) || [],
            },
          };
        }),
      );
  }

  changePassword(clave: string) {
    const refreshToken = this.cookie.get(REFRESH_TOKEN);
    const url = this.url + 'changePassword';
    return this.http
      .put<CustomResponse<boolean>>(url, { clave, refreshToken })
      .pipe(
        catchError((err) => handleError(err)),
        map((data) => data.result),
      );
  }

  checkPass(clave: string) {
    const url = this.url + 'checkPass';
    return this.http
      .post<boolean>(url, { clave })
      .pipe(catchError((err) => handleError(err)));
  }

  registerMultiple(data: Partial<Usuario>[]) {
    const url = this.url + 'registerMultiple';
    return this.http
      .post<
        CustomResponse<{
          newUsuarios: Usuario[];
          updateUsuarios: Usuario[];
          errorUsuarios: {
            cuil: string;
            email: string;
            error: string;
          }[];
        }>
      >(url, data)
      .pipe(
        catchError((err) => handleError(err)),
        map((data) => data.result),
      );
  }

  bajaMultiple(cuils: string[]) {
    const url = environment.api + 'usuarios/' + 'bajaMultiple';
    return this.http
      .patch<CustomResponse<{ ok: boolean }>>(url, { cuils })
      .pipe(
        catchError((err) => handleError(err)),
        map((data) => data.result),
      );
  }

  setSuperadmin(id_usuario: number) {
    const url = this.url + 'setSuperadmin/' + id_usuario;
    return this.http.patch<CustomResponse<Usuario>>(url, {}).pipe(
      catchError((err) => handleError(err)),
      map((data) => data.result),
    );
  }
}
